import { House, User, ClockCounterClockwise, Blueprint, Tag } from '@phosphor-icons/react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { LoteKanban } from '@/types';
import { contrastingText } from '@/lib/utils';
import { useAppStore } from '@/store/appStore';
import clsx from 'clsx';

interface Props {
  lote: LoteKanban;
  // Cuando true, la tarjeta es un clon estatico (la usa el DragOverlay).
  // Sin DnD listeners ni transformaciones; solo el preview visual.
  overlay?: boolean;
}

// Semaforo del avance del estado actual:
//   verde   = todavia esta dentro del tiempo proyectado
//   naranja = se paso pero por poco (1.5x del tiempo proyectado)
//   rojo    = muy atrasado
// Si no hay diasProyectados o no hay historico, devuelve null y la tarjeta
// muestra solo el "Sin datos" o el dia X sin badge.
function semaforo(diasEnEstado: number | null | undefined, diasProyectados: number | null | undefined) {
  if (diasEnEstado == null || diasProyectados == null || diasProyectados <= 0) return null;
  const ratio = diasEnEstado / diasProyectados;
  if (ratio <= 1) return { color: '#1D9E75', label: `${diasEnEstado}/${diasProyectados}d` };
  if (ratio <= 1.5) return { color: '#D85A30', label: `${diasEnEstado}/${diasProyectados}d` };
  return { color: '#BB4A4A', label: `${diasEnEstado}/${diasProyectados}d` };
}

export function LoteCard({ lote, overlay = false }: Props) {
  const sem = semaforo(lote.diasEnEstado, lote.diasProyectados);
  const estadoColor = lote.colorEstadoActual ?? null;
  const estadoText = lote.estadoActual ?? null;
  const selectLote = useAppStore(s => s.selectLote);

  // Hook se llama siempre (rule-of-hooks); en modo overlay el id es
  // distinto para no colisionar con la tarjeta original que sigue montada
  // mientras se arrastra.
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: overlay ? `lote-overlay-${lote.idLote}` : `lote-${lote.idLote}`,
    data: { type: 'lote', lote },
    disabled: overlay,
  });

  const style = overlay
    ? undefined
    : {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.4 : 1,
      };

  return (
    <div
      ref={overlay ? undefined : setNodeRef}
      style={style}
      {...(overlay ? {} : attributes)}
      {...(overlay ? {} : listeners)}
      onClick={overlay ? undefined : (e) => {
        // El activationConstraint distance:6 garantiza que un click puro
        // (sin movimiento) NO arranca el drag — si llegamos aca con
        // !isDragging es porque fue click real.
        if (!isDragging) {
          e.stopPropagation();
          selectLote(lote.idLote);
        }
      }}
      className={clsx(
        'rounded-md border border-stone-200 bg-white px-2.5 py-2 text-xs',
        'hover:border-stone-400 hover:shadow-sm transition-all select-none',
        overlay ? 'shadow-lg cursor-grabbing' : 'cursor-grab'
      )}
    >
      <div className="flex items-start gap-1.5 mb-1.5">
        <House size={12} weight="bold" className="text-stone-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-stone-800 truncate">{lote.idBd || lote.lote || `Lote ${lote.idLote}`}</div>
          {/* Modelo: tramitado es la jerarquia primaria (icono Blueprint).
              El reservado (icono Tag) aparece como linea secundaria cuando
              ambos existen y difieren — o como unica linea cuando solo hay
              reservado (lote sin tramitar todavia). */}
          {lote.modeloTramitado && (
            <div className="text-[11px] text-stone-600 truncate flex items-center gap-1">
              <Blueprint size={10} weight="bold" className="text-stone-500 flex-shrink-0" />
              <span className="truncate">{lote.modeloTramitado}</span>
            </div>
          )}
          {lote.modeloReservado &&
            lote.modeloReservado !== lote.modeloTramitado && (
              <div
                className="text-[10px] text-stone-500 truncate flex items-center gap-1"
                title="Modelo reservado (caso vigente)"
              >
                <Tag size={10} weight="bold" className="text-stone-400 flex-shrink-0" />
                <span className="truncate">{lote.modeloReservado}</span>
              </div>
            )}
        </div>
      </div>

      {(lote.proyecto || lote.nomBloque || lote.nomFase) && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {lote.proyecto && (
            <span className="bg-brand-gray-100 text-brand-gray-400 rounded-full px-2 py-0.5 text-[10px] font-medium">
              {lote.proyecto}
            </span>
          )}
          {lote.nomBloque && (
            <span className="bg-brand-gray-100 text-brand-gray-400 rounded-full px-2 py-0.5 text-[10px] font-medium">
              {lote.nomBloque}
            </span>
          )}
          {lote.nomFase && (
            <span className="bg-brand-gray-100 text-brand-gray-400 rounded-full px-2 py-0.5 text-[10px] font-medium">
              {lote.nomFase}
            </span>
          )}
        </div>
      )}

      {estadoText && (
        <div className="mb-1.5">
          <span
            className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{
              background: estadoColor ?? '#888780',
              color: estadoColor ? contrastingText(estadoColor) : '#fff'
            }}
          >
            {estadoText}
          </span>
          {lote.descripcionEstadoActual && (
            <div
              className="text-[11px] text-stone-700 mt-1 leading-snug font-medium line-clamp-3"
              title={lote.descripcionEstadoActual}
            >
              {lote.descripcionEstadoActual}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-1.5">
        {lote.nomCreadorApc ? (
          <div className="flex items-center gap-1 text-[10px] text-stone-500 min-w-0">
            <User size={10} weight="bold" className="flex-shrink-0" />
            <span className="truncate">{lote.nomCreadorApc}</span>
          </div>
        ) : (
          <span />
        )}
        {sem ? (
          <span
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-mono font-bold"
            style={{ background: sem.color, color: '#fff' }}
            title="Dias en estado actual / dias proyectados"
          >
            <ClockCounterClockwise size={10} weight="bold" />
            {sem.label}
          </span>
        ) : lote.diasEnEstado != null ? (
          <span className="text-[10px] text-stone-400 font-mono">{lote.diasEnEstado}d</span>
        ) : null}
      </div>
    </div>
  );
}
